import { getAuthHeaders } from "../../../utils/authHeaders";

export type BullionLobbyMatchesResponse = {
    matches: unknown[];
    count: number;
    maxMatches?: number;
};

const BULLION_LOBBY_HTTP_CACHE_TTL_MS = 45_000;

type LobbyHttpCacheEntry = {
    expiresAt: number;
    payload: BullionLobbyMatchesResponse;
};

let bullionLobbyHttpCache: LobbyHttpCacheEntry | null = null;
let bullionLobbyHttpInflight: Promise<BullionLobbyMatchesResponse> | null = null;

/** In-memory hit for warm starts (same SPA session, within TTL). */
export function peekBullionLobbyMatchesCache(): BullionLobbyMatchesResponse | null {
    const now = Date.now();
    if (bullionLobbyHttpCache && bullionLobbyHttpCache.expiresAt > now) return bullionLobbyHttpCache.payload;
    return null;
}

/**
 * Bullion lobby list: same rows as GET `/bullion/lobby-matches` (fixtures with frozen `bullion_match.opening_odds` only).
 */
export async function fetchBullionLobbyMatches(): Promise<BullionLobbyMatchesResponse> {
    const base = (process.env.API_URL || "").replace(/\/$/, "");
    const url = `${base}/bullion/lobby-matches`;
    const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
    if (!res.ok) return { matches: [], count: 0 };
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const matches = Array.isArray(data.matches) ? data.matches : [];
    const count = typeof data.count === "number" ? data.count : matches.length;
    const maxMatches = typeof data.maxMatches === "number" ? data.maxMatches : undefined;
    return { matches, count, maxMatches };
}

export function invalidateBullionLobbyMatchesCache(): void {
    bullionLobbyHttpCache = null;
    bullionLobbyHttpInflight = null;
}

/**
 * Cached GET `/bullion/lobby-matches` — speeds up match landing and avoids duplicate in-flight requests.
 */
export async function fetchBullionLobbyMatchesCached(opts?: {
    bypassCache?: boolean;
}): Promise<BullionLobbyMatchesResponse> {
    const now = Date.now();
    if (!opts?.bypassCache) {
        if (bullionLobbyHttpCache && bullionLobbyHttpCache.expiresAt > now) {
            return bullionLobbyHttpCache.payload;
        }
        if (bullionLobbyHttpInflight) return bullionLobbyHttpInflight;
    }

    bullionLobbyHttpInflight = (async () => {
        try {
            const payload = await fetchBullionLobbyMatches();
            bullionLobbyHttpCache = {
                expiresAt: Date.now() + BULLION_LOBBY_HTTP_CACHE_TTL_MS,
                payload,
            };
            return payload;
        } finally {
            bullionLobbyHttpInflight = null;
        }
    })();

    return bullionLobbyHttpInflight;
}
